/* Partner Document Vault · Major Custodian Applied Kit — download kit, file the signed Acknowledgement. */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, FileDown, Upload, Loader2, FileCheck2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

export function CustodianKitVaultSection({ api }) {
  const [kit, setKit] = useState(null);
  const [busy, setBusy] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(() => {
    api.get("/custodian-kit/mine").then((r) => setKit(r.data)).catch(() => setKit(null));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  if (!kit) return null;

  const downloadKit = async () => {
    setBusy("kit");
    try {
      const r = await api.get("/custodian-kit/mine.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `${kit.ref}-Major-Custodian-Applied-Kit.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the kit"); }
    finally { setBusy(null); }
  };

  const uploadAck = async (file) => {
    if (!file) return;
    setBusy("ack");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/custodian-kit/mine/acknowledgement", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Signed Acknowledgement filed · ${data.acknowledgement.srn}`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(null); if (inputRef.current) inputRef.current.value = ""; }
  };

  const ack = kit.acknowledgement;
  return (
    <section className="mt-14 pt-10 border-t border-white/10" data-testid="vault-custodian-kit">
      <div className="flex items-center gap-3 mb-2">
        <span className="grid place-items-center w-9 h-9 rounded-full bg-[#C9A961] text-black"><ShieldCheck size={17} /></span>
        <h2 className="font-serif text-2xl md:text-3xl">Major Custodian Applied Kit</h2>
      </div>
      <p className="font-body text-sm text-[#8A8A82] mt-2 mb-6 max-w-2xl leading-relaxed">
        Issued by Vinci Inc. under reference <span className="text-[#C9C9C2]" style={{ fontFamily: MONO }}>{kit.ref}</span>, effective {kit.effective_date}.
        Major Custodian: <span className="text-[#C9C9C2]">{kit.custodian}</span>. Download the kit, have the Acknowledgement page signed by{" "}
        <span className="text-[#C9C9C2]">{kit.signatory}</span>, and file the signed page here — it is stored permanently in your vault.
        {kit.srn && <> Service request <span className="text-[#C9A961]" style={{ fontFamily: MONO }}>{kit.srn}</span>.</>}
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-sm border border-white/10 p-5 flex flex-col gap-4">
          <div className="flex items-start gap-4">
            <span className="grid place-items-center w-11 h-11 rounded-full bg-[#141414] border border-[#C9A961]/50 text-[#C9A961] shrink-0"><FileDown size={18} /></span>
            <div>
              <div className="font-serif text-lg leading-snug">The Kit · 5 documents</div>
              <p className="font-body text-xs text-[#8A8A82] mt-1">Executive Summary · Activation Certificate · Scope of Authority · Flowchart · Acknowledgement</p>
            </div>
          </div>
          <button data-testid="vault-custodian-kit-download" onClick={downloadKit} disabled={!!busy}
                  className="self-start inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:border-[#C9A961] disabled:opacity-50">
            {busy === "kit" ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />} Download Kit PDF
          </button>
        </div>
        <div className="rounded-sm border p-5 flex flex-col gap-4" style={{ borderColor: ack ? "rgba(47,185,153,0.6)" : "rgba(255,255,255,0.1)" }} data-testid="vault-custodian-ack">
          <div className="flex items-start gap-4">
            <span className="grid place-items-center w-11 h-11 rounded-full shrink-0 border" style={{ color: ack ? "#2FB999" : "#C9A961", borderColor: ack ? "rgba(47,185,153,0.6)" : "rgba(201,169,97,0.5)", background: "#141414" }}><FileCheck2 size={18} /></span>
            <div>
              <div className="font-serif text-lg leading-snug">Signed Acknowledgement</div>
              <p className="font-body text-xs text-[#8A8A82] mt-1" data-testid="vault-custodian-ack-status">
                {ack ? `Filed ${new Date(ack.uploaded_at).toLocaleString("en-IN")} · ${ack.srn}` : `Awaiting signature of ${kit.signatory} · upload PDF, PNG or JPG`}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {ack && (
              <a data-testid="vault-custodian-ack-download" href={`${API}${kit.ack_url}`} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-2 rounded-full bg-[#2FB999] text-black px-5 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:opacity-90">
                <FileDown size={13} /> Open signed page
              </a>
            )}
            <label className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:border-[#C9A961] cursor-pointer">
              {busy === "ack" ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {ack ? "Re-upload" : "Upload signed page"}
              <input ref={inputRef} data-testid="vault-custodian-ack-input" type="file" accept="application/pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => uploadAck(e.target.files?.[0])} />
            </label>
          </div>
        </div>
      </div>
    </section>
  );
}
